import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AddIcon from '@mui/icons-material/Add';
import SpeedIcon from '@mui/icons-material/Speed';
import ReplyIcon from '@mui/icons-material/Reply';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import facturaVentaService, { FacturaVenta, resolveMasterNumber } from '../../services/facturaVentaService';
import sucVentaService from '../../services/sucVentaService';
import remitoVentaService, { RemitoVentaCreateDto } from '../../services/remitoVentaService';
import ventasSeguimientoService from '../../services/ventasSeguimientoService';
import mercadoPagoService from '../../services/mercadoPagoService';
import PDFViewer from '../../components/shared/PDFViewer';
import QRPaymentDialog from '../../components/shared/QRPaymentDialog';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { format } from 'date-fns';
import { useRef } from 'react';

type RemitoStatus = 'unknown' | 'none' | 'full' | 'partial';
type RemitoDraftItem = {
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  codigoArticulo: string;
  descripcionArticulo: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc: number;
  notas?: string;
};

const Facturas: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [facturas, setFacturas] = useState<FacturaVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const desde = searchParams.get('fechaDesde');
    if (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) return desde;
    return format(getFirstDayOfMonth(), 'yyyy-MM-dd');
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const hasta = searchParams.get('fechaHasta');
    if (hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta)) return hasta;
    return format(getLastDayOfMonth(), 'yyyy-MM-dd');
  });
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedFactura, setSelectedFactura] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [selectedFacturaForPayment, setSelectedFacturaForPayment] = useState<number | null>(null);
  const [obteniendoCae, setObteniendoCae] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [sucVentaMap, setSucVentaMap] = useState<Record<number, { generaRemito: string; nroSucRemito?: number }>>({});
  const [generandoRemito, setGenerandoRemito] = useState<number | null>(null);
  const [remitoStatusMap, setRemitoStatusMap] = useState<Record<number, RemitoStatus>>({});
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; facturaId: number } | null>(null);
  const [remitoActionsAnchor, setRemitoActionsAnchor] = useState<{ el: HTMLElement; facturaId: number } | null>(null);
  const remitoStatusCacheRef = useRef<Map<number, RemitoStatus>>(new Map());
  const remitoStatusPendingRef = useRef<Map<number, Promise<RemitoStatus>>>(new Map());

  const mapEstadoFacturaParam = (v: string): string | undefined => {
    if (!v) return undefined;
    if (v === 'conCae') return 'ConCae';
    if (v === 'sinCae') return 'SinCae';
    if (v === 'interno') return 'Interno';
    return undefined;
  };

  const fetchFacturas = useCallback(async (
    pageNumber: number,
    pageSize: number,
    search: string,
    desde: string,
    hasta: string,
    estado: string
  ) => {
    try {
      setLoading(true);
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const response = await facturaVentaService.getFacturasVenta({
        pageNumber,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined,
        estadoFactura: mapEstadoFacturaParam(estado),
      });

      setFacturas(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      console.error('Error al cargar facturas:', error);
      const errorMessage = getApiErrorMessage(error, 'Error al cargar las facturas');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000
      });
      setFacturas([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Sincronizar fechas desde URL cuando cambian los searchParams (ej. al llegar desde dashboard)
  useEffect(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    if (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) setFechaDesde(desde);
    if (hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta)) setFechaHasta(hasta);
    setPage(0);
  }, [searchParams]);

  useEffect(() => {
    fetchFacturas(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, fetchFacturas]);

  useEffect(() => {
    const loadSucVentas = async () => {
      try {
        // API SucVenta limita PageSize a 20; cargar todas las páginas para el mapa de generaRemito
        const allItems: Array<{ nroSuc: number; generaRemito?: string; nroSucRemito?: number }> = [];
        let page = 1;
        const pageSize = 20;
        let hasMore = true;
        while (hasMore) {
          const res = await sucVentaService.getSucVentas({
            pageNumber: page,
            pageSize,
            nroTipoFormulario: '1',
          });
          allItems.push(...res.items);
          hasMore = res.items.length === pageSize && page * pageSize < (res.totalCount ?? 0);
          page++;
        }
        const map: Record<number, { generaRemito: string; nroSucRemito?: number }> = {};
        allItems.forEach((sv) => {
          map[sv.nroSuc] = {
            generaRemito: sv.generaRemito ?? 'N',
            nroSucRemito: sv.nroSucRemito,
          };
        });
        setSucVentaMap(map);
      } catch (err) {
        console.warn('No se pudo cargar SucVentas para botón Generar remito:', err);
      }
    };
    loadSucVentas();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const validRowsPerPage = Math.max(1, Math.min(20, parseInt(event.target.value, 10)));
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      setPdfLoading(nroFormulario);
      const response = await api.get(
        `/api/FacturasVenta/${nroFormulario}/pdf`,
        {
          responseType: 'blob',
        }
      );
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedFactura(nroFormulario);
    } catch (error: any) {
      console.error('Error al generar el PDF de la factura:', error);
      const errorMessage = getErrorMessage(error, 'Error al generar el PDF de la factura');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedFactura(null);
    setPdfFileName(null);
  };

  const getErrorMessage = (error: any, defaultMessage: string) => {
    if (error?.response?.data) {
      let data = error.response.data;
      
      // Si viene como string, intentar parsearlo como JSON
      if (typeof data === 'string') {
        try {
          // Si es un string JSON, parsearlo
          if (data.trim().startsWith('{') || data.trim().startsWith('[')) {
            data = JSON.parse(data);
          } else {
            // Si es un string simple, devolverlo directamente
            return data;
          }
        } catch {
          // Si no es JSON válido, devolver el string tal cual
          return data;
        }
      }
      
      // Si ahora es un objeto, buscar el mensaje en diferentes propiedades
      if (typeof data === 'object' && data !== null) {
        // Buscar en orden de prioridad: mensaje (español), message (inglés), error, detail, title
        const mensaje = 
          data.mensaje || 
          data.message || 
          data.error || 
          data.detail || 
          data.title ||
          data.Mensaje ||
          data.Message;
        
        if (mensaje) {
          return mensaje;
        }
        
        // Si tiene una propiedad con el mensaje anidado, intentar extraerlo
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          return data.errors[0].message || data.errors[0].mensaje || data.errors[0];
        }
      }
    }
    
    // Si no se encontró un mensaje específico, usar el mensaje del error o el default
    return error?.message || defaultMessage;
  };

  const handleGenerarLinkPago = async (nroFormulario: number) => {
    try {
      setQrLoading(true);
      setSelectedFacturaForPayment(nroFormulario);
      setQrDialogOpen(true);
      setQrCode(null);
      setPaymentLink(null);

      const response = await mercadoPagoService.getPaymentLink(nroFormulario);
      setQrCode(response.qrCode || response.paymentLink);
      setPaymentLink(response.paymentLink);
    } catch (error: any) {
      console.error('Error al generar link de pago:', error);
      const errorMessage = getErrorMessage(error, 'Error al generar el link de pago');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 3000
      });
      setQrDialogOpen(false);
    } finally {
      setQrLoading(false);
    }
  };

  const handleCloseQRDialog = () => {
    setQrDialogOpen(false);
    setQrCode(null);
    setPaymentLink(null);
    setSelectedFacturaForPayment(null);
  };

  const handleObtenerCae = async (nroFormulario: number) => {
    try {
      setObteniendoCae(nroFormulario);
      const facturaActualizada = await facturaVentaService.obtenerCaeArgentina(nroFormulario);
      
      // Actualizar la factura en la lista con todos los campos relevantes
      setFacturas(prevFacturas => 
        prevFacturas.map(f => 
          f.nroFormulario === nroFormulario 
            ? { 
                ...f, 
                nroComprobante: facturaActualizada.nroComprobante,
                caeNro: facturaActualizada.caeNro, 
                caeVencimiento: facturaActualizada.caeVencimiento,
                caeResultado: facturaActualizada.caeResultado,
                caeObs: facturaActualizada.caeObs
              }
            : f
        )
      );
      
      enqueueSnackbar('CAE obtenido exitosamente', { variant: 'success' });
    } catch (error: any) {
      console.error('Error al obtener CAE:', error);
      const errorMessage = getErrorMessage(error, 'Error al obtener el CAE');
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 8000
      });
    } finally {
      setObteniendoCae(null);
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '-';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      return date.toLocaleDateString('es-AR');
    } catch {
      return typeof dateString === 'string' ? dateString : '-';
    }
  };

  const formatComprobante = (factura: FacturaVenta) => {
    const { letra, nroSuc, nroComprobante } = factura;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  const esFacturaNoCategorizada = (factura: FacturaVenta) => {
    const tipoIva = (factura.tipoIva ?? factura.tipoCliente ?? '').toString().trim().toUpperCase();
    return tipoIva === 'N';
  };

  const handleFacturarFacturaNoCategorizada = (nroFormulario: number) => {
    if (!nroFormulario) return;
    navigate(`/ventas/facturas/nuevo?facturaId=${nroFormulario}`);
  };

  const handleAnular = async (facturaId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (window.confirm('¿Está seguro que desea anular esta factura?')) {
      try {
        await facturaVentaService.anularFactura(facturaId);
        enqueueSnackbar('Factura anulada correctamente', { variant: 'success' });
        fetchFacturas(page + 1, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado);
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.response?.data || 'Error al anular la factura';
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      }
    }
  };

  const puedeGenerarRemitoPorSucursal = (factura: FacturaVenta) => {
    if (factura.nroFormularioRemito != null && factura.nroFormularioRemito > 0) return false;
    const nroSuc = factura.nroSuc ?? 0;
    const sv = sucVentaMap[nroSuc];
    return sv?.generaRemito === 'S';
  };

  const setRemitoStatus = (nroFormulario: number, status: RemitoStatus) => {
    remitoStatusCacheRef.current.set(nroFormulario, status);
    setRemitoStatusMap((prev) => ({ ...prev, [nroFormulario]: status }));
  };

  const getRemitoStatus = useCallback(async (nroFormulario: number, force = false): Promise<RemitoStatus> => {
    if (!force) {
      const cached = remitoStatusCacheRef.current.get(nroFormulario);
      if (cached) return cached;
      const pending = remitoStatusPendingRef.current.get(nroFormulario);
      if (pending) return pending;
    }

    const promise = (async (): Promise<RemitoStatus> => {
      try {
        const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
        if (!puedeGenerarRemitoPorSucursal(factura)) {
          setRemitoStatus(nroFormulario, 'none');
          return 'none';
        }
        if (!factura.detalles || factura.detalles.length === 0) {
          setRemitoStatus(nroFormulario, 'none');
          return 'none';
        }

        const keys = new Map<string, { masterFormulario: number; masterItem: number }>();
        factura.detalles.forEach((d, index) => {
          const masterFormulario = resolveMasterNumber(d as any, [
            'nroFormularioMaster',
            'nroformulariomaster',
            'NroFormularioMaster',
            'nro_formulario_master',
            'nroformulario',
            'NroFormulario',
          ]) ?? nroFormulario;
          const masterItem = resolveMasterNumber(d as any, [
            'nroItemMaster',
            'nroitemmaster',
            'NroItemMaster',
            'nro_item_master',
            'nroitem',
            'NroItem',
          ]) ?? Number((d as any).nroItem ?? index + 1);
          const key = `${masterFormulario}-${masterItem}`;
          if (!keys.has(key)) {
            keys.set(key, { masterFormulario, masterItem });
          }
        });

        if (keys.size === 0) {
          setRemitoStatus(nroFormulario, 'none');
          return 'none';
        }

        const hilos = await Promise.all(
          Array.from(keys.values()).map((k) =>
            ventasSeguimientoService.getHilo(k.masterFormulario, k.masterItem)
          )
        );

        let pendienteTotal = 0;
        let tieneRemitosPrevios = false;
        hilos.forEach((h) => {
          const cantidades = (h.comprobantes || []).reduce(
            (acc, c) => {
              const tipo = String(c.nroTipoFormulario ?? '').trim();
              const cantidad = Number(c.cantidad ?? 0);
              if (tipo === '1' || tipo === '2') acc.facturada += cantidad;
              if (tipo === '3') acc.devuelta += cantidad;
              if (tipo === '9') acc.remitida += cantidad;
              return acc;
            },
            { facturada: 0, devuelta: 0, remitida: 0 }
          );
          const cantidadFacturadaNeta = cantidades.facturada - cantidades.devuelta;
          const pendienteEntregar = Math.max(0, cantidadFacturadaNeta - cantidades.remitida);
          pendienteTotal += pendienteEntregar;
          if (cantidades.remitida > 0) {
            tieneRemitosPrevios = true;
          }
        });

        const status: RemitoStatus = pendienteTotal <= 0 ? 'none' : (tieneRemitosPrevios ? 'partial' : 'full');
        setRemitoStatus(nroFormulario, status);
        return status;
      } catch (error) {
        console.warn(`No se pudo calcular estado de remito para factura ${nroFormulario}:`, error);
        setRemitoStatus(nroFormulario, 'unknown');
        return 'unknown';
      } finally {
        remitoStatusPendingRef.current.delete(nroFormulario);
      }
    })();

    remitoStatusPendingRef.current.set(nroFormulario, promise);
    return promise;
  }, [sucVentaMap]);

  const invalidateRemitoStatus = (nroFormulario: number) => {
    remitoStatusCacheRef.current.delete(nroFormulario);
    remitoStatusPendingRef.current.delete(nroFormulario);
    setRemitoStatusMap((prev) => {
      const next = { ...prev };
      delete next[nroFormulario];
      return next;
    });
  };

  const resolveDetalleMaster = (detalle: any, index: number, nroFormularioDefault: number) => {
    const nroFormularioMaster = resolveMasterNumber(detalle as any, [
      'nroFormularioMaster',
      'nroformulariomaster',
      'NroFormularioMaster',
      'nro_formulario_master',
      'nroformulario',
      'NroFormulario',
    ]) ?? nroFormularioDefault;
    const nroItemMaster = resolveMasterNumber(detalle as any, [
      'nroItemMaster',
      'nroitemmaster',
      'NroItemMaster',
      'nro_item_master',
      'nroitem',
      'NroItem',
    ]) ?? Number((detalle as any).nroItem ?? index + 1);
    return { nroFormularioMaster, nroItemMaster };
  };

  const buildRemitoParcialDraft = async (nroFormulario: number): Promise<{
    nroFormularioFactura: number;
    formulario: {
      nroCliente: number;
      nombre?: string;
      cuit?: string;
      nroLista?: string;
      nroSuc: number;
      descripcion?: string;
      nroUbicacionOrigen?: number;
      porcDesc?: number | null;
      porcDesc2?: number | null;
      porcDesc3?: number | null;
    };
    items: RemitoDraftItem[];
  }> => {
    const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
    const nroSuc = factura.nroSuc ?? 0;
    const sucVenta = await sucVentaService.getSucVenta(nroSuc, '1');

    if (sucVenta.generaRemito !== 'S') {
      throw new Error('Esta sucursal no permite generar remitos desde facturas');
    }
    if (!factura.nroCliente || factura.nroCliente <= 0) {
      throw new Error('La factura debe tener un cliente asignado');
    }
    if (!factura.detalles || factura.detalles.length === 0) {
      throw new Error('La factura no tiene items para generar remito');
    }

    const hiloCache = new Map<string, Awaited<ReturnType<typeof ventasSeguimientoService.getHilo>>>();
    const pendingItems: RemitoDraftItem[] = [];

    for (let index = 0; index < factura.detalles.length; index++) {
      const d = factura.detalles[index];
      const { nroFormularioMaster, nroItemMaster } = resolveDetalleMaster(d, index, nroFormulario);
      const key = `${nroFormularioMaster}-${nroItemMaster}`;
      if (!hiloCache.has(key)) {
        const hilo = await ventasSeguimientoService.getHilo(nroFormularioMaster, nroItemMaster);
        hiloCache.set(key, hilo);
      }
      const hilo = hiloCache.get(key);
      const cantidades = (hilo?.comprobantes || []).reduce(
        (acc, c) => {
          const tipo = String(c.nroTipoFormulario ?? '').trim();
          const cantidad = Number(c.cantidad ?? 0);
          if (tipo === '1' || tipo === '2') acc.facturada += cantidad;
          if (tipo === '3') acc.devuelta += cantidad;
          if (tipo === '9') acc.remitida += cantidad;
          return acc;
        },
        { facturada: 0, devuelta: 0, remitida: 0 }
      );
      const pendienteEntregar = Math.max(0, (cantidades.facturada - cantidades.devuelta) - cantidades.remitida);
      if (pendienteEntregar <= 0) continue;

      pendingItems.push({
        nroFormularioMaster,
        nroItemMaster,
        codigoArticulo: String(d.codigoArticulo ?? '').trim(),
        descripcionArticulo: d.descripcionArticulo ?? '',
        cantidad: Number(pendienteEntregar),
        precioUnitario: Number(d.precioUnitario ?? 0),
        porcDesc: Number(d.porcDesc ?? 0),
        notas: d.notas ?? '',
      });
    }

    return {
      nroFormularioFactura: nroFormulario,
      formulario: {
        nroCliente: factura.nroCliente,
        nombre: factura.nombre,
        cuit: factura.cuit,
        nroLista: factura.nroLista,
        nroSuc: sucVenta.nroSucRemito ?? factura.nroSuc ?? 0,
        descripcion: factura.descripcion ?? `Remito parcial desde factura ${nroFormulario}`,
        nroUbicacionOrigen: (factura as any).nroUbicacionOrigen ?? undefined,
        porcDesc: factura.porcDesc ?? 0,
        porcDesc2: factura.porcDesc2 ?? 0,
        porcDesc3: factura.porcDesc3 ?? 0,
      },
      items: pendingItems,
    };
  };

  const getRemitoButtonState = (factura: FacturaVenta) => {
    const status = remitoStatusMap[factura.nroFormulario] ?? 'unknown';
    if (status === 'partial') {
      return { show: true, title: 'Generar remito parcial', bg: '#f57c00', hover: '#ef6c00' };
    }
    if (status === 'full') {
      return { show: true, title: 'Generar remito', bg: '#795548', hover: '#6d4c41' };
    }
    return { show: false, title: 'Generar remito', bg: '#795548', hover: '#6d4c41' };
  };

  useEffect(() => {
    const visibles = facturas.filter((f) => puedeGenerarRemitoPorSucursal(f));
    visibles.forEach((f) => {
      if (!remitoStatusCacheRef.current.has(f.nroFormulario) && !remitoStatusPendingRef.current.has(f.nroFormulario)) {
        void getRemitoStatus(f.nroFormulario);
      }
    });
  }, [facturas, getRemitoStatus, sucVentaMap]);

  const handleGenerarRemito = async (nroFormulario: number) => {
    try {
      setGenerandoRemito(nroFormulario);
      const estadoRemito = await getRemitoStatus(nroFormulario);
      if (estadoRemito === 'none') {
        enqueueSnackbar('La factura no tiene cantidades pendientes de remitir', { variant: 'error' });
        return;
      }
      const factura = await facturaVentaService.getFacturaVenta(nroFormulario);
      const nroSuc = factura.nroSuc ?? 0;
      const sucVenta = await sucVentaService.getSucVenta(nroSuc, '1');
      if (sucVenta.generaRemito !== 'S') {
        enqueueSnackbar('Esta sucursal no permite generar remitos desde facturas', { variant: 'error' });
        return;
      }
      if (!factura.detalles || factura.detalles.length === 0) {
        enqueueSnackbar('La factura no tiene items para generar el remito', { variant: 'error' });
        return;
      }
      if (!factura.nroCliente || factura.nroCliente <= 0) {
        enqueueSnackbar('La factura debe tener un cliente asignado', { variant: 'error' });
        return;
      }
      const ahora = new Date();
      const payload: RemitoVentaCreateDto = {
        formulario: {
          nroCliente: factura.nroCliente,
          nombre: factura.nombre,
          cuit: factura.cuit,
          nroLista: factura.nroLista,
          nroSuc: sucVenta.nroSucRemito ?? factura.nroSuc ?? 0,
          descripcion: factura.descripcion ?? `Remito desde factura ${nroFormulario}`,
          nroFormularioFactura: nroFormulario,
          fechaEmision: ahora.toISOString(),
          horaEmision: ahora.toISOString(),
          fechaVencim: ahora.toISOString(),
          nroUbicacionOrigen: (factura as any).nroUbicacionOrigen ?? undefined,
          porcDesc: factura.porcDesc ?? 0,
          porcDesc2: factura.porcDesc2 ?? 0,
          porcDesc3: factura.porcDesc3 ?? 0,
        },
        items: factura.detalles.map((d) => {
          const nroFormularioMaster = resolveMasterNumber(d as any, [
            'nroFormularioMaster',
            'nroformulariomaster',
            'NroFormularioMaster',
            'nro_formulario_master',
          ]);
          const nroItemMaster = resolveMasterNumber(d as any, [
            'nroItemMaster',
            'nroitemmaster',
            'NroItemMaster',
            'nro_item_master',
          ]);
          return {
            codigoArticulo: String(d.codigoArticulo ?? '').trim(),
            descripcion: d.descripcionArticulo ?? '',
            cantidad: Number(d.cantidad ?? 0),
            precioUnitario: Number(d.precioUnitario ?? 0),
            porcDesc: d.porcDesc ?? 0,
            notas: d.notas ?? '',
            nroFormularioMaster,
            nroItemMaster,
          };
        }),
      };
      const remitoCreado = await remitoVentaService.createRemitoVenta(payload);
      enqueueSnackbar(`Remito generado correctamente (Nro: ${remitoCreado.nroFormulario})`, { variant: 'success' });
      invalidateRemitoStatus(nroFormulario);
      void getRemitoStatus(nroFormulario, true);
      navigate('/ventas/remitos');
    } catch (error: any) {
      const msg = getApiErrorMessage(error, 'Error al generar el remito');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setGenerandoRemito(null);
    }
  };

  const handleAbrirRemitoParcial = async (nroFormulario: number) => {
    try {
      setGenerandoRemito(nroFormulario);
      const estadoRemito = await getRemitoStatus(nroFormulario);
      if (estadoRemito === 'none') {
        enqueueSnackbar('La factura no tiene cantidades pendientes de remitir', { variant: 'error' });
        return;
      }

      const draft = await buildRemitoParcialDraft(nroFormulario);
      if (!draft.items.length) {
        enqueueSnackbar('No hay items con pendiente para generar remito parcial', { variant: 'warning' });
        return;
      }

      navigate('/ventas/remitos/nuevo', {
        state: {
          remitoParcialDesdeFactura: draft,
        },
      });
    } catch (error: any) {
      const msg = getApiErrorMessage(error, 'Error al preparar el remito parcial');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setGenerandoRemito(null);
    }
  };

  const openRemitoActions = (event: React.MouseEvent<HTMLElement>, facturaId: number) => {
    event.stopPropagation();
    setRemitoActionsAnchor({ el: event.currentTarget, facturaId });
  };

  const closeRemitoActions = () => setRemitoActionsAnchor(null);

  const getEstadoChip = (factura: FacturaVenta) => {
    const tipoIva = (factura.tipoIva ?? factura.tipoCliente ?? '').toString().trim().toUpperCase();
    if (tipoIva === 'N') {
      return { label: 'Interno', bgColor: '#ECEFF1', textColor: '#455A64' };
    }
    if (factura.caeNro) {
      return { label: 'Con CAE', bgColor: '#C8E6C9', textColor: '#2E7D32' };
    }
    return { label: 'Sin CAE', bgColor: '#FFE0B2', textColor: '#E65100' };
  };

  const handleAbrirFacturaPos = (nroFormulario: number) => {
    navigate(`/ventas/facturas/pos?nroFormulario=${nroFormulario}`);
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, facturaId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, facturaId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const puedeEditarFactura =
    Boolean(user?.roles.includes('SUPERADMIN') || user?.roles.includes('BACKDOOR') || user?.roles.includes('SOPORTE-IT'));

  const btnSx = (bgColor: string, hoverColor: string) => ({
    bgcolor: bgColor,
    color: '#fff',
    borderRadius: 1.5,
    width: 32,
    height: 32,
    '&:hover': { bgcolor: hoverColor, transform: 'scale(1.1)' },
    '&:disabled': { bgcolor: '#ccc' },
    transition: 'all 0.2s',
  });

  const currentKebabFactura = kebabAnchor
    ? facturas.find((f) => f.nroFormulario === kebabAnchor.facturaId)
    : null;

  const renderKebabFacturaItems = (factura: FacturaVenta) => {
    const items: React.ReactElement[] = [];
    if (esFacturaNoCategorizada(factura)) {
      items.push(
        <MenuItem key="facturar" onClick={() => { handleKebabClose(); handleFacturarFacturaNoCategorizada(factura.nroFormulario); }}>
          <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Facturar con CAE</ListItemText>
        </MenuItem>
      );
    }
    if (puedeEditarFactura) {
      items.push(
        <MenuItem key="editar" onClick={() => { handleKebabClose(); navigate(`/ventas/facturas/${factura.nroFormulario}/editar`); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
      );
    }
    items.push(
      <MenuItem key="nc" onClick={() => { handleKebabClose(); navigate(`/ventas/notas-credito/nuevo?facturaId=${factura.nroFormulario}`); }}>
        <ListItemIcon><ReplyIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Nota de crédito</ListItemText>
      </MenuItem>
    );
    if (!factura.caeNro) {
      items.push(
        <MenuItem key="anular" onClick={() => { handleKebabClose(); void handleAnular(factura.nroFormulario); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Anular factura</ListItemText>
        </MenuItem>
      );
    }
    items.push(
      <MenuItem key="mp" onClick={() => { handleKebabClose(); void handleGenerarLinkPago(factura.nroFormulario); }}>
        <ListItemIcon><QrCodeIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Link de pago (Mercado Pago)</ListItemText>
      </MenuItem>
    );
    return items;
  };

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Facturas de Venta
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/ventas/facturas/nuevo')}
                sx={{ borderRadius: 2, px: 2.5, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Nueva Factura
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SpeedIcon />}
                onClick={() => navigate('/ventas/facturas/pos')}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' }
                }}
              >
                Nueva Factura POS
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar por número, cliente, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filtroEstado}
                    label="Estado"
                    onChange={(e) => { setFiltroEstado(e.target.value); setPage(0); }}
                    sx={{ borderRadius: 2, bgcolor: '#fafafa' }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="conCae">Con CAE</MenuItem>
                    <MenuItem value="sinCae">Sin CAE</MenuItem>
                    <MenuItem value="interno">Interno</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {facturas.map((factura) => {
                const estadoChip = getEstadoChip(factura);
                const comprobante = formatComprobante(factura);
                return (
                  <Card 
                    key={factura.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              component="span"
                              variant="subtitle1"
                              sx={{ ...comprobanteSx, fontWeight: 600, display: 'block' }}
                              onClick={() => handleAbrirFacturaPos(factura.nroFormulario)}
                            >
                              {comprobante !== '-' ? comprobante : `Factura #${factura.nroFormulario}`}
                            </Typography>
                          </Box>
                          {estadoChip.label ? (
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{
                                bgcolor: estadoChip.bgColor,
                                color: estadoChip.textColor,
                                fontWeight: 500,
                                borderRadius: 1.5,
                              }}
                            />
                          ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {formatDate(factura.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cliente:</strong> {factura.nombre?.trim() || '-'}
                        </Typography>
                        {factura.cuit?.trim() ? (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {factura.cuit.trim()}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe Total:</strong> {formatCurrency(factura.importeTotal)}
                        </Typography>
                        {factura.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {factura.usuario}
                          </Typography>
                        )}
                        {factura.caeNro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {factura.caeNro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                          {!factura.caeNro && (
                            <Tooltip title="Obtener CAE de Argentina">
                              <IconButton
                                onClick={() => handleObtenerCae(factura.nroFormulario)}
                                size="small"
                                disabled={obteniendoCae === factura.nroFormulario}
                                sx={btnSx('#2196f3', '#1976d2')}
                              >
                                {obteniendoCae === factura.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <CloudDownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {(() => {
                            const remitoBtn = getRemitoButtonState(factura);
                            if (!remitoBtn.show) return null;
                            return (
                            <Tooltip title={remitoBtn.title}>
                              <IconButton
                                onMouseEnter={() => { void getRemitoStatus(factura.nroFormulario); }}
                                onClick={(e) => openRemitoActions(e, factura.nroFormulario)}
                                size="small"
                                disabled={generandoRemito === factura.nroFormulario}
                                sx={btnSx(remitoBtn.bg, remitoBtn.hover)}
                              >
                                {generandoRemito === factura.nroFormulario ? (
                                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                                ) : (
                                  <LocalShippingIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            );
                          })()}
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(factura.nroFormulario)}
                              size="small"
                              disabled={pdfLoading === factura.nroFormulario}
                              sx={btnSx('#2196f3', '#1976d2')}
                            >
                              {pdfLoading === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Más acciones">
                            <IconButton
                              size="small"
                              onClick={(e) => handleKebabOpen(e, factura.nroFormulario)}
                              sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && facturas.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay facturas para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                  width: '100%'
                }
              }}
            >
          <Table
            size="medium"
            sx={{
              minWidth: 720,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                padding: { xs: '8px 4px', sm: '10px 8px', md: '12px 8px' }
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nro. Factura</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facturas.map((factura) => {
                const estadoChip = getEstadoChip(factura);
                return (
                  <TableRow 
                    key={factura.nroFormulario} 
                    sx={{
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{factura.nroFormulario}</TableCell>
                    <TableCell>{formatDate(factura.fechaEmision)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                      <Typography component="span" sx={comprobanteSx} onClick={() => handleAbrirFacturaPos(factura.nroFormulario)}>
                        {formatComprobante(factura)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{factura.nombre?.trim() || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>{formatCurrency(factura.importeTotal)}</TableCell>
                    <TableCell>
                      {estadoChip.label ? (
                        <Chip
                          label={estadoChip.label}
                          size="small"
                          sx={{
                            bgcolor: estadoChip.bgColor,
                            color: estadoChip.textColor,
                            fontWeight: 500,
                            borderRadius: 1.5,
                            minWidth: 90,
                          }}
                        />
                      ) : ''}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                        {!factura.caeNro && (
                          <Tooltip title="Obtener CAE de Argentina">
                            <IconButton
                              onClick={() => handleObtenerCae(factura.nroFormulario)}
                              size="small"
                              disabled={obteniendoCae === factura.nroFormulario}
                              sx={btnSx('#2196f3', '#1976d2')}
                            >
                              {obteniendoCae === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <CloudDownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {(() => {
                          const remitoBtn = getRemitoButtonState(factura);
                          if (!remitoBtn.show) return null;
                          return (
                          <Tooltip title={remitoBtn.title}>
                            <IconButton
                              onMouseEnter={() => { void getRemitoStatus(factura.nroFormulario); }}
                              onClick={(e) => openRemitoActions(e, factura.nroFormulario)}
                              size="small"
                              disabled={generandoRemito === factura.nroFormulario}
                              sx={btnSx(remitoBtn.bg, remitoBtn.hover)}
                            >
                              {generandoRemito === factura.nroFormulario ? (
                                <CircularProgress size={16} sx={{ color: '#fff' }} />
                              ) : (
                                <LocalShippingIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          );
                        })()}
                        <Tooltip title="Imprimir PDF">
                          <IconButton
                            onClick={() => handleImprimirPDF(factura.nroFormulario)}
                            size="small"
                            disabled={pdfLoading === factura.nroFormulario}
                            sx={btnSx('#2196f3', '#1976d2')}
                          >
                            {pdfLoading === factura.nroFormulario ? (
                              <CircularProgress size={16} sx={{ color: '#fff' }} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Más acciones">
                          <IconButton
                            size="small"
                            onClick={(e) => handleKebabOpen(e, factura.nroFormulario)}
                            sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
              {loading && facturas.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && facturas.length === 0 && (
                <Box sx={{ py: 4 }}>
                  <Typography align="center" color="text.secondary">
                    No hay facturas para mostrar
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabFactura && renderKebabFacturaItems(currentKebabFactura)}
      </Menu>

      <Menu
        anchorEl={remitoActionsAnchor?.el}
        open={Boolean(remitoActionsAnchor)}
        onClose={closeRemitoActions}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            const facturaId = remitoActionsAnchor?.facturaId;
            closeRemitoActions();
            if (facturaId) void handleGenerarRemito(facturaId);
          }}
        >
          <ListItemIcon><LocalShippingIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Generar remito total</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const facturaId = remitoActionsAnchor?.facturaId;
            closeRemitoActions();
            if (facturaId) void handleAbrirRemitoParcial(facturaId);
          }}
        >
          <ListItemIcon><LocalShippingIcon fontSize="small" sx={{ color: '#f57c00' }} /></ListItemIcon>
          <ListItemText>Generar remito parcial</ListItemText>
        </MenuItem>
      </Menu>

      {pdfUrl && selectedFactura && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Factura ${selectedFactura}`}
          documentId={`${selectedFactura}`}
          documentType="factura"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      {qrDialogOpen && selectedFacturaForPayment && (
        <QRPaymentDialog
          open={qrDialogOpen}
          onClose={handleCloseQRDialog}
          qrCode={qrCode}
          paymentLink={paymentLink}
          facturaNumero={selectedFacturaForPayment}
          loading={qrLoading}
        />
      )}
    </Box>
  );
};

export default Facturas;

